/**
 * 2D几何计算工具类
 * 提供通用的2D几何计算功能
 */

import { Vec2d, VecLike } from './Vec2d';
import { LineSegmentLike } from './LineSegment';
import { CircleSegment, CircleSegmentLike } from './CircleSegment';

/**
 * 几何计算工具类
 */
export class GeometryUtils {
  /**
   * 计算两点之间的距离
   */
  static distance(p1: VecLike, p2: VecLike): number {
    return Vec2d.Dist(p1, p2);
  }

  /**
   * 计算点到圆弧的距离
   */
  static pointToCircleDistance(point: VecLike, circleSegment: CircleSegmentLike): number {
    const arc = circleSegment instanceof CircleSegment 
      ? circleSegment 
      : CircleSegment.fromJson(circleSegment);
    return arc.distanceToPoint(point);
  }

  /**
   * 计算点到线段的距离
   */
  static pointToLineDistance(point: VecLike, lineSegment: LineSegmentLike): number {
    return Vec2d.DistanceToLineSegment(lineSegment.start, lineSegment.end, point);
  }

  /**
   * 获取距离点最近的线段
   * @param point 点
   * @param lineSegments 线段集合
   * @returns 距离点最近的线段
   */
  static getNearestLineSegment(point: VecLike, lineSegments: LineSegmentLike[]): LineSegmentLike | null {
    let nearestLineSegment: LineSegmentLike | null = null;
    let minDistance = Infinity;
    for (const lineSegment of lineSegments) {
      const distance = this.pointToLineDistance(point, lineSegment);
      if (distance < minDistance) {
        minDistance = distance;
        nearestLineSegment = lineSegment;
      }
    }
    return nearestLineSegment;
  }

  /**
   * 获取某个点在某条直线上的基于某个轴的投影点
   * @param point 点
   * @param lineSegment 线段(内部需要把线段转换成直线, 然后计算投影点)
   * @param axis 轴
   * @returns 投影点
   */
  static getProjectionPoint(point: VecLike, lineSegment: LineSegmentLike, axis: 'x' | 'y'): Vec2d {
    const { start, end } = lineSegment;
    
    // 将线段转换为直线，计算直线的方向向量
    const direction = Vec2d.Tan(end, start);
    
    if (axis === 'x') {
      // 基于x轴的投影：保持x坐标不变，y坐标投影到直线上
      if (Math.abs(direction.x) < 1e-10) {
        // 垂直线，投影到直线上任意点的x坐标
        return new Vec2d(point.x, start.y);
      }
      
      // 使用向量方法计算投影
      // 计算从起点到目标点的向量
      const toPoint = Vec2d.Sub(point, start);
      // 计算在x轴方向上的投影参数
      const t = toPoint.x / direction.x;
      // 计算投影点
      const projectedPoint = Vec2d.Add(start, Vec2d.Mul(direction, t));
      // 保持x坐标不变
      return new Vec2d(point.x, projectedPoint.y);
    } else {
      // 基于y轴的投影：保持y坐标不变，x坐标投影到直线上
      if (Math.abs(direction.y) < 1e-10) {
        // 水平线，投影到直线上任意点的y坐标
        return new Vec2d(start.x, point.y);
      }
      
      // 使用向量方法计算投影
      // 计算从起点到目标点的向量
      const toPoint = Vec2d.Sub(point, start);
      // 计算在y轴方向上的投影参数
      const t = toPoint.y / direction.y;
      // 计算投影点
      const projectedPoint = Vec2d.Add(start, Vec2d.Mul(direction, t));
      // 保持y坐标不变
      return new Vec2d(projectedPoint.x, point.y);
    }
  }

  /**
   * 获取投影点并沿垂直于线段的方向偏移指定距离
   * @param point 点
   * @param lineSegment 线段
   * @param axis 投影轴
   * @param offsetDistance 偏移距离
   * @param tolerance 容差
   * @returns 偏移后的投影点
   */
  static getProjectionPointWithOffset(
    point: VecLike, 
    lineSegment: LineSegmentLike, 
    axis: 'x' | 'y', 
    offsetDistance: number,
    tolerance:number = 0.1,
  ): Vec2d {

    // 计算线段的方向向量
    const { start, end } = lineSegment;
    const direction = Vec2d.Tan(end, start);
    // 计算垂直于线段的方向向量（法向量）
    const normal = Vec2d.Per(direction);
    const axisVector = axis === 'x' ? new Vec2d(1, 0) : new Vec2d(0, 1);
    // 判断normal和directionVector的向量是否相交
    const cross = Math.round(Vec2d.Cpr(normal, axisVector) * 100 / 100);
    let _axis = axis;
    if (cross === 0) {
      _axis = axis === 'x' ? 'y' : 'x';
    }
    // 首先获取投影点
    const projectionPoint = this.getProjectionPoint(point, lineSegment, _axis);
    
    // 沿法向量方向偏移指定距离
    const offsetVector = Vec2d.Mul(normal, offsetDistance);
    const offsetPoint = Vec2d.Add(projectionPoint, offsetVector);
    // 最后offsetPoint中的点x,y需要取tolerance容差位数, 比如0.1, 则需要取小数点后一位, 0.01, 则需要取小数点后两位
    const toleranceFactor = 1 / tolerance;
    const offsetPointX = Math.round(offsetPoint.x * toleranceFactor) / toleranceFactor;
    const offsetPointY = Math.round(offsetPoint.y * toleranceFactor) / toleranceFactor;
    return new Vec2d(offsetPointX, offsetPointY);
  }

  /**
   * 计算点到直线的距离
   */
  static pointToLineThroughPointDistance(point: VecLike, linePoint: VecLike, lineDirection: VecLike): number {
    return Vec2d.DistanceToLineThroughPoint(linePoint, lineDirection, point);
  }

  /**
   * 获取线段上最近的点
   */
  static nearestPointOnLineSegment(lineStart: VecLike, lineEnd: VecLike, point: VecLike, clamp = true): Vec2d {
    return Vec2d.NearestPointOnLineSegment(lineStart, lineEnd, point, clamp);
  }

  /**
   * 计算两条线段的交点
   */
  static lineIntersection(
    line1Start: VecLike, line1End: VecLike,
    line2Start: VecLike, line2End: VecLike,
  ): Vec2d | null {
    const x1 = line1Start.x, y1 = line1Start.y;
    const x2 = line1End.x, y2 = line1End.y;
    const x3 = line2Start.x, y3 = line2Start.y;
    const x4 = line2End.x, y4 = line2End.y;

    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(denom) < 1e-10) return null; // 平行线

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return new Vec2d(
        x1 + t * (x2 - x1),
        y1 + t * (y2 - y1),
      );
    }

    return null;
  }

  /**
   * 判断点是否在多边形内
   */
  static pointInPolygon(point: VecLike, polygon: VecLike[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      if (((polygon[i].y > point.y) !== (polygon[j].y > point.y)) &&
          (point.x < (polygon[j].x - polygon[i].x) * (point.y - polygon[i].y) / (polygon[j].y - polygon[i].y) + polygon[i].x)) {
        inside = !inside;
      }
    }
    return inside;
  }

  /**
   * 计算多边形的质心
   */
  static polygonCentroid(points: VecLike[]): Vec2d {
    if (points.length === 0) return new Vec2d(0, 0);
    if (points.length === 1) return new Vec2d(points[0].x, points[0].y);

    let area = 0;
    let cx = 0;
    let cy = 0;

    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      const cross = points[i].x * points[j].y - points[j].x * points[i].y;
      area += cross;
      cx += (points[i].x + points[j].x) * cross;
      cy += (points[i].y + points[j].y) * cross;
    }

    area /= 2;
    const factor = 1 / (6 * area);

    return new Vec2d(cx * factor, cy * factor);
  }

  /**
   * 计算包围盒
   */
  static boundingBox(points: VecLike[]): { min: Vec2d; max: Vec2d } {
    if (points.length === 0) {
      return { min: new Vec2d(0, 0), max: new Vec2d(0, 0) };
    }

    let minX = points[0].x;
    let minY = points[0].y;
    let maxX = points[0].x;
    let maxY = points[0].y;

    for (const point of points) {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    }

    return {
      min: new Vec2d(minX, minY),
      max: new Vec2d(maxX, maxY),
    };
  }

  /**
   * 判断三点是否顺时针排列
   */
  static isClockwise(A: VecLike, B: VecLike, C: VecLike): boolean {
    return Vec2d.Clockwise(A, B, C);
  }

  /**
   * 计算三点形成的角度
   */
  static angleBetweenPoints(A: VecLike, B: VecLike, C: VecLike): number {
    const xa = B.x - A.x;
    const xb = C.x - B.x;
    const ya = B.y - A.y;
    const yb = C.y - B.y;
    let angle = 0;
    const _a = Math.sqrt(xa * xa + ya * ya);
    const _b = Math.sqrt(xb * xb + yb * yb);
    if (_a && _b) {
      const p = xa * xb + ya * yb;
      angle = Math.acos(p / (_a * _b));
      angle = angle / Math.PI * 180;
      // direction 大于0 逆时针, 小于0 顺时针, 等于0 平行
      let direction = xa * yb - ya * xb;
      direction = direction > 0 ? 1 : -1;
      angle = 180 + direction * angle;
    }
    return angle;
  }

  /**
   * 根据圆心和半径，获取圆上的等份点
   */
  static getCirclePoints(center: VecLike, radius: number, segments: number = 16): Vec2d[] {
    const points: Vec2d[] = [];
    const angleStep = (2 * Math.PI) / segments;

    for (let i = 0; i < segments; i++) {
      const angle = i * angleStep;
      const x = center.x + radius * Math.cos(angle);
      const y = center.y + radius * Math.sin(angle);
      points.push(new Vec2d(x, y));
    }

    return points;
  }

  /**
   * 根据圆心和起始点，获取圆弧上的等份点
   */
  static getArcPoints(
    center: VecLike,
    startPoint: VecLike,
    endAngle: number,
    segments: number = 8,
  ): Vec2d[] {
    const points: Vec2d[] = [];
    const startAngle = Math.atan2(startPoint.y - center.y, startPoint.x - center.x);
    const angleStep = endAngle / segments;

    for (let i = 0; i <= segments; i++) {
      const angle = startAngle + i * angleStep;
      const x = center.x + Vec2d.Dist(center, startPoint) * Math.cos(angle);
      const y = center.y + Vec2d.Dist(center, startPoint) * Math.sin(angle);
      points.push(new Vec2d(x, y));
    }

    return points;
  }

  /**
   * 计算点到点的中点
   */
  static midpoint(p1: VecLike, p2: VecLike): Vec2d {
    return Vec2d.Med(p1, p2);
  }

  /**
   * 计算多个点的平均值
   */
  static average(points: VecLike[]): Vec2d {
    return Vec2d.Average(points);
  }

  /**
   * 判断两个点是否接近
   */
  static isNear(p1: VecLike, p2: VecLike, tolerance: number = 0.0001): boolean {
    return Vec2d.Equals(p1, p2, tolerance);
  }

  // /**
  //  * 转换线段
  //  * @param lineSegment 线段
  //  * @param matrix 矩阵
  //  * @returns 转换后的线段
  //  */
  // static transformLineSegment(lineSegment: LineSegmentLike, matrix: Matrix2D): LineSegmentLike {
  //   return 
  // }

} 